// src/context/AuthContext.tsx
import React, { createContext, useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { login as loginAction, logout as logoutAction } from '../store/slices/authSlice';
import { fetchSystemSettings } from '../store/slices/systemSettingsSlice';
import { User } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  userRole: string | null;
  isClient: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated, isLoading, error } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchSystemSettings());
    }
  }, [isAuthenticated, dispatch]);

  const userRole = user?.role?.name || null;
  const isClient = user?.isClient || user?.role?.name === 'Client' || user?.role?.name === 'Site Engineer' || false;

  const login = async (credentials: { email: string; password: string }) => {
    await dispatch(loginAction(credentials));
  };

  const logout = () => {
    dispatch(logoutAction());
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      userRole, 
      isClient,
      isLoading,
      error,
      login, 
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};